/* I tipi del dominio come arrivano dal backend, e le loro etichette.
 *
 * **I nomi degli enum sono quelli del backend**, non tradotti: `tipo` vale
 * "BILANCIO", non "Bilancio". Le etichette leggibili stanno qui sotto in una
 * mappa separata, e la ragione e' che confonderle e' il modo piu' rapido di
 * mandare al server una stringa che lui non conosce. Quello che si vede e
 * quello che si spedisce restano due cose diverse.
 *
 * `Importo` e' una stringa e resta una stringa: vedi `api.ts`.
 */

export type TipoPratica =
  | "CONTABILITA"
  | "DICHIARAZIONE_REDDITI"
  | "BILANCIO"
  | "CONSULENZA"
  | "CONTENZIOSO"
  | "PRATICHE_CAMERALI"
  | "CONSULENZA_LAVORO"
  | "ELABORAZIONE_DATI"
  | "ASSISTENZA_ORDINARIA";

export type StatoPratica = "APERTA" | "SOSPESA" | "CHIUSA";

export type Territorialita = "ITALIA" | "PRIVATO_UE" | "PRIVATO_EXTRA_UE";

/** Gli otto tipi li ha dettati lo studio il 19 settembre 2026, non li ha
 * inventati il programma; il nono, l'assistenza ordinaria, l'ha chiesto lo
 * studio il 25 settembre. Se ne nasce uno nuovo, lo dice lo studio. */
export const TIPI_PRATICA: { valore: TipoPratica; nome: string }[] = [
  { valore: "CONTABILITA", nome: "Contabilità" },
  { valore: "DICHIARAZIONE_REDDITI", nome: "Dichiarazione dei redditi" },
  { valore: "BILANCIO", nome: "Bilancio" },
  { valore: "CONSULENZA", nome: "Consulenza" },
  { valore: "CONTENZIOSO", nome: "Contenzioso" },
  { valore: "PRATICHE_CAMERALI", nome: "Pratiche camerali" },
  { valore: "CONSULENZA_LAVORO", nome: "Consulenza del lavoro" },
  { valore: "ELABORAZIONE_DATI", nome: "Elaborazione dati" },
  { valore: "ASSISTENZA_ORDINARIA", nome: "Assistenza ordinaria" },
];

/** Minuscolo e senza accenti: "Società" si trova scrivendo "societa". Serve a
 * ogni ricerca del programma, cosi' cercano tutte allo stesso modo. */
export function normale(testo: string): string {
  return testo.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/** La pratica come la legge una persona: "Bianchi Costruzioni SRL, bilancio
 * 2026", piu' la descrizione solo se dice qualcosa che il tipo non dice gia'.
 * L'etichetta del server resta quella lunga, perche' finisce anche sui
 * documenti; questa e' solo per lo schermo. */
export function etichettaBreve(p: Pratica): string {
  const tipo = nomeTipo(p.tipo);
  const descrizione = p.descrizione.trim();
  const aggiunta =
    descrizione && !normale(descrizione).includes(normale(tipo)) ? `, ${descrizione}` : "";
  return `${p.cliente.denominazione}, ${tipo.toLowerCase()} ${p.periodo}${aggiunta}`;
}

/** "3 fabbricati", "1 fabbricato": il plurale italiano dei casi regolari, che
 * sono quelli delle unita' di un listino. Una parola irregolare resta com'e'. */
export function quanteUnita(quanti: number, unita: string): string {
  if (quanti === 1 || !unita) return `${quanti} ${unita}`.trim();
  const plurali: Record<string, string> = { o: "i", a: "e", e: "i" };
  const finale = unita.slice(-1);
  const plurale = plurali[finale] ? unita.slice(0, -1) + plurali[finale] : unita;
  return `${quanti} ${plurale}`;
}

/** Ogni parola cercata deve comparire da qualche parte nei campi dati. */
export function trova(cerca: string, ...campi: string[]): boolean {
  const parole = normale(cerca).split(/\s+/).filter(Boolean);
  if (parole.length === 0) return true;
  const testo = normale(campi.join(" "));
  const compatto = testo.replace(/\s+/g, "");
  return parole.every((p) => testo.includes(p) || compatto.includes(p));
}

export const TERRITORIALITA: { valore: Territorialita; nome: string }[] = [
  { valore: "ITALIA", nome: "Italia" },
  { valore: "PRIVATO_UE", nome: "Privato residente in UE" },
  { valore: "PRIVATO_EXTRA_UE", nome: "Privato residente fuori UE" },
];

export function nomeTipo(tipo: string): string {
  return TIPI_PRATICA.find((t) => t.valore === tipo)?.nome ?? tipo;
}

export function nomeStato(stato: string): string {
  const nomi: Record<string, string> = {
    APERTA: "aperta",
    SOSPESA: "sospesa",
    CHIUSA: "chiusa",
  };
  return nomi[stato] ?? stato;
}

export interface Cliente {
  identificativo: string;
  denominazione: string;
  codice: string;
  sede: string;
  indirizzo: string;
  codice_fiscale: string;
  partita_iva: string;
  /** Dove lo SDI consegna le sue fatture: serve alla scheda per Fatture Smart. */
  codice_destinatario: string;
  pec: string;
  sostituto_imposta: boolean;
  pubblica_amministrazione: boolean;
  split_payment: boolean;
  territorialita: Territorialita;
}

export interface Pratica {
  identificativo: string;
  cliente: Cliente;
  tipo: TipoPratica;
  periodo: number;
  descrizione: string;
  stato: StatoPratica;
  aperta_il: string | null;
  chiusa_il: string | null;
  responsabile: string;
  dimensione: number | null;
  unita_dimensione: string;
  dimensione_misurata: boolean;
  prezzo_concordato: string | null;
  etichetta: string;
}

export interface Attivita {
  codice: string;
  descrizione: string;
  tipi_pratica: TipoPratica[];
  /** Prezzo fisso, come stringa. `null`: si fattura a ore. */
  prezzo: string | null;
  /** Se il prezzo e' per unita' ("fabbricato"), di quale. Vuota altrimenti. */
  unita: string;
  /** La frase con cui compare nel testo da incollare in Fatture Smart. Vuota:
   * vale la descrizione. */
  testo_fattura: string;
  /** Proposta da chi imputa e non ancora rivista da chi fattura. */
  da_rivedere: boolean;
}

/** Una prestazione del catalogo che somiglia a quella che si sta per proporre. */
export interface PrestazioneSimile {
  codice: string;
  descrizione: string;
  tipi_pratica: string[];
  /** Stesse parole che contano: e' la stessa prestazione scritta in modo diverso. */
  identica: boolean;
  /** Vale gia' per il tipo di lavoro richiesto. */
  stesso_tipo: boolean;
  da_rivedere: boolean;
}

/** Quanto e quando e' usato un codice del catalogo (conteggi per codice, non per persona). */
export interface UsoAttivita {
  codice: string;
  registrazioni: number;
  minuti: number;
  ultima: string | null;
}

/** Un codice proposto da chi imputa, nella coda di chi fattura. */
export interface CodiceDaRivedere {
  codice: string;
  descrizione: string;
  tipi_pratica: string[];
  proposto_da: string;
  proposto_il: string;
  imputazioni: number;
  minuti: number;
  simili: PrestazioneSimile[];
}

export interface Imputazione {
  identificativo: string;
  pratica_id: string;
  persona: string;
  giorno: string;
  minuti: number;
  quantita: number;
  attivita_codice: string;
  oggetto: string;
  scopo: string;
  inserita_il: string | null;
  ritardo_giorni: number | null;
  in_giornata: boolean | null;
  fatturata: boolean;
  fatturata_con: string | null;
}

/** Una riga del registro delle ore: l'imputazione con i nomi gia' risolti dal
 * server, perche' chi imputa non ha accesso all'elenco delle persone. */
export interface RigaRegistro extends Imputazione {
  persona_nome: string;
  cliente_id: string | null;
  cliente: string;
  /** "Bilancio 2026, bilancio di esercizio": senza il cliente. */
  pratica: string;
  attivita: string;
}

export interface Registro {
  righe: RigaRegistro[];
  minuti: number;
  minuti_da_fatturare: number;
  persone: { identificativo: string; nome: string }[];
  /** Vero se chi guarda vede le ore di tutti, falso se solo le sue. */
  tutte: boolean;
}

export type StatoFatturazione = "tutte" | "da_fatturare" | "fatturate";

export interface FiltroRegistro {
  dal?: string;
  al?: string;
  cliente_id?: string;
  pratica_id?: string;
  persona?: string;
  attivita?: string;
  testo?: string;
  stato?: StatoFatturazione;
}

export interface GiornataSospetta {
  giorno: string;
  minuti: number;
}

export interface Rapporto {
  persona: string;
  persona_nome: string;
  imputazioni: number;
  minuti: number;
  ore: string;
  in_giornata: number;
  in_ritardo: number;
  senza_data_di_inserimento: number;
  quota_in_giornata: number | null;
  giornate_sospette: GiornataSospetta[];
}

export interface BucoNumerazione {
  anno: number;
  serie: string;
  numeri: number[];
}

export interface Tariffa {
  id: number;
  oraria: string;
  dal: string;
  persona: string | null;
  tipo_pratica: TipoPratica | null;
  cliente_id: string | null;
  nota: string;
  specificita: number;
}

/** Cosa cambia al lavoro non ancora fatturato se si salva una tariffa. */
export interface EffettoTariffa {
  /** Le pratiche il cui valore cambia (o che restano senza prezzo). */
  pratiche: number;
  minuti: number;
  prima: string;
  dopo: string;
  differenza: string;
  senza_tariffa_prima: number;
  senza_tariffa_dopo: number;
}

export interface AnteprimaTariffa {
  /** Esiste gia' la stessa tariffa per lo stesso giorno: salvare ne cambia la cifra. */
  corregge: boolean;
  cifra_attuale: string | null;
  precedente: string | null;
  /** Se non e' `null`, salvare chiede una conferma esplicita e questo e' il motivo. */
  conferma: string | null;
  effetto: EffettoTariffa;
}

/** Una riga dell'elenco delle modifiche a listino e catalogo, per il titolare. */
export interface ModificaListino {
  istante: string;
  chi: string;
  cosa: string;
  dettaglio: string;
}

export interface VoceMaturato {
  attivita_codice: string;
  minuti: number;
  valore: string;
}

export interface Maturato {
  pratica_id: string;
  cliente_id: string;
  etichetta: string;
  minuti: number;
  valore: string;
  voci: VoceMaturato[];
  imputazioni: string[];
  senza_tariffa: string[];
  prezzo_concordato: string | null;
  prezzo_gia_fatturato: boolean;
  scostamento: string | null;
  da_fatturare: string;
  /** Il giorno del lavoro piu' vecchio ancora da fatturare: da quanto aspetta. `null` senza ore. */
  dal: string | null;
}

export interface FasciaAnzianita {
  etichetta: string;
  fino_a_giorni: number | null;
  valore: string;
  pratiche: number;
}

export interface PraticaFerma {
  pratica_id: string;
  cliente_id: string;
  etichetta: string;
  da_fatturare: string;
  dal: string | null;
  giorni: number | null;
  chiusa_il: string | null;
}

export interface Anzianita {
  fasce: FasciaAnzianita[];
  senza_data: string;
  /** Il da fatturare di tutte le pratiche: lo stesso di `Maturato.da_fatturare` sommato. */
  totale: string;
  /** Le registrazioni senza tariffa, escluse dal totale finche' il listino non le copre. */
  senza_tariffa: number;
  ferme: PraticaFerma[];
}

/** Cinque numeri per il cruscotto: se c'e' qualcosa di cui parlare e se manca qualcosa da rivedere. */
export interface StatoAvvio {
  clienti: number;
  prestazioni: number;
  /** Prestazioni proposte da un operatore e non ancora riviste da chi fattura. */
  da_rivedere: number;
  documenti: number;
  documenti_numerati: boolean;
}

export type TipoDocumento = "AVVISO" | "FATTURA" | "NOTA_CREDITO";
export type StatoDocumento = "BOZZA" | "EMESSO" | "ANNULLATO" | "INCASSATO" | "STORNATO";

export interface Voce {
  descrizione: string;
  importo: string;
}

export interface Calcolo {
  compenso_prestazioni: string;
  spese_forfettarie: string;
  cassa: string;
  base_iva: string;
  iva: string;
  spese_art_15: string;
  bollo_riaddebitato: string;
  bollo_dovuto: string;
  totale_documento: string;
  ritenuta: string;
  iva_a_carico_pa: string;
  netto_a_pagare: string;
  natura_iva: string | null;
  avvisi: string[];
  quadra: boolean;
}

export interface Documento {
  identificativo: string;
  tipo: TipoDocumento;
  stato: StatoDocumento;
  data: string;
  creato_il: string | null;
  numero: string | null;
  riferimento: string | null;
  origine: string | null;
  cliente_id: string | null;
  intestatario: string;
  descrizione: string;
  note: string[];
  righe: (Voce & { pratica_id: string | null })[];
  spese: Voce[];
  forfettarie: Voce[];
  calcolo: Calcolo;
  netto_a_pagare: string;
  totale_documento: string;
  prospetto: string;
  /** Per un avviso: quanto e' stato versato, quanto manca e i versamenti. Per
   * gli altri documenti `residuo` e' `null` e l'elenco e' vuoto. */
  incassato: string;
  residuo: string | null;
  incassi: Incasso[];
}

export type NomeModalita = "CONTANTI" | "BONIFICO" | "ASSEGNO" | "CARTA" | "ALTRO";

/** Come ha pagato il cliente. I nomi sono quelli che il server accetta. */
export const MODALITA: { valore: NomeModalita; nome: string }[] = [
  { valore: "BONIFICO", nome: "Bonifico" },
  { valore: "CONTANTI", nome: "Contanti" },
  { valore: "ASSEGNO", nome: "Assegno" },
  { valore: "CARTA", nome: "Carta o POS" },
  { valore: "ALTRO", nome: "Altro" },
];

/** Un versamento di un cliente su un avviso. Un versamento sbagliato non si
 * cancella: si annulla e resta visibile, col motivo. */
export interface Incasso {
  identificativo: string;
  /** I versamenti dello stesso gesto (50 contanti, 50 bonifico) condividono il
   * gruppo: e' l'unita' per cui si fa una fattura. */
  gruppo: string;
  documento_id: string;
  data: string;
  importo: string;
  modalita: NomeModalita;
  modalita_nome: string;
  nota: string;
  registrato_da_nome: string;
  registrato_il: string | null;
  /** Non ha prodotto una fattura da solo: va passata a mano su Fatture Smart. */
  da_fatturare_a_mano: boolean;
  fattura_riferimento: string;
  fatturato_il: string | null;
  annullato: boolean;
  annullato_il: string | null;
  annullato_motivo: string;
  annullato_da_nome: string;
}

/** Un incasso che aspetta la sua fattura fatta a mano, con la scadenza dei 12 giorni. */
export interface VersamentoDaFatturare {
  gruppo: string;
  documento_id: string;
  intestatario: string;
  descrizione: string;
  data: string;
  importo: string;
  modalita: string[];
  giorni_di_attesa: number;
  urgenza: "in_tempo" | "avviso" | "scaduto";
  residuo_avviso: string;
  avviso_chiuso: boolean;
}

/** La fattura di un acconto o di un saldo: testo e cifre di quel versamento, non dell'avviso intero. */
export interface FatturaDelVersamento {
  gruppo: string;
  documento_id: string;
  intestatario: string;
  descrizione: string;
  data: string;
  scadenza: string;
  giorni_di_attesa: number;
  urgenza: "in_tempo" | "avviso" | "scaduto";
  importo_versato: string;
  modalita: string[];
  saldo: boolean;
  avviso_chiuso: boolean;
  residuo_avviso: string;
  fattura_riferimento: string;
  testo: string;
  importo_riga: string;
  iva: string;
  controllo: { etichetta: string; valore: string }[];
  totale_atteso: string;
  nota_conti: string;
  da_guardare: string[];
}

/** Una riga del controllo di gestione: un cliente, un tipo di pratica, una pratica o una persona. */
export interface RigaGestione {
  chiave: string;
  nome: string;
  minuti: number;
  minuti_fatturati: number;
  ricavo: string;
  valore_fatturato: string;
  da_fatturare: string;
  /** Ricavo di un'ora fatturata. Non e' un margine: il costo del personale non e' nel programma. */
  euro_per_ora: string | null;
  /** 1,00 e' in linea col listino; sotto, venduto sotto listino. */
  rispetto_tariffa: string | null;
  senza_tariffa: number;
  giorni_con_ore: number;
  /** Costo delle ore fatturate (costo orario pieno del dipendente per le sue ore). */
  costo: string;
  /** Costo gia' sostenuto sulle ore non ancora fatturate. */
  costo_da_recuperare: string;
  /** Fatturato meno costo. Vuoto se manca un costo orario: un margine su un costo mancante e' sbagliato. */
  margine: string | null;
  margine_percentuale: string | null;
  in_perdita: boolean;
  senza_costo: number;
  giorni_incasso: number | null;
}

export interface Gestione {
  anno: number;
  /** L'intervallo calcolato, estremi compresi: l'anno solare se non ne e' stato chiesto un altro. */
  dal: string;
  al: string;
  anni: number[];
  totale: RigaGestione;
  clienti: RigaGestione[];
  tipi: RigaGestione[];
  pratiche: RigaGestione[];
  persone: RigaGestione[];
  attivita: RigaGestione[];
  /** Una riga per sede del cliente; chi non l'ha compilata sta in "Senza sede". */
  sedi: RigaGestione[];
  primi_clienti_quanti: number;
  /** Quanta parte del fatturato fanno i primi clienti (0,62 = il 62%); null se non dice niente. */
  primi_clienti_quota: string | null;
  /** Chi ha lavorato nell'anno senza un costo orario: il margine non e' completo finche' manca. */
  persone_senza_costo: string[];
}

/** Una pratica a prezzo concordato non ancora fatturata, con quanto del prezzo e' gia' costo. */
export interface PraticaAPrezzo {
  pratica_id: string;
  etichetta: string;
  cliente_id: string;
  cliente: string;
  stato: string;
  prezzo: string;
  /** Il costo delle ore che hanno un costo orario: un minimo se `senza_costo` e' maggiore di zero. */
  costo: string;
  minuti: number;
  senza_costo: number;
  /** Costo / prezzo: 0,85 vuol dire che l'85% del prezzo e' gia' costo. */
  quota: string;
  sforata: boolean;
}

export interface PrezzoConcordato {
  /** Le pratiche a prezzo concordato non ancora fatturate che hanno gia' delle ore. */
  monitorate: number;
  soglia_in_vista: string;
  soglia_sforata: string;
  /** Quelle da tenere d'occhio, dalla piu' avanti. */
  pratiche: PraticaAPrezzo[];
}

/** Un cliente nel periodo scelto: il lavoro, gli avvisi, l'incassato e i due buchi. */
export interface ClientePeriodo {
  cliente_id: string;
  nome: string;
  minuti: number;
  valore_ore: string;
  da_fatturare: string;
  senza_tariffa: number;
  avvisi: number;
  importo_avvisi: string;
  incassato: string;
  da_incassare: string;
}

export interface AvvisoPeriodo {
  documento_id: string;
  cliente_id: string;
  cliente: string;
  data: string;
  descrizione: string;
  stato: string;
  compenso: string;
  totale_documento: string;
  netto: string;
  /** Tutto cio' che il cliente ha versato su questo avviso, in qualunque data. */
  versato: string;
  /** Quanto manca: zero se l'avviso e' incassato. */
  residuo: string;
  fattura: string;
}

export interface IncassoPeriodo {
  incasso_id: string;
  data: string;
  cliente_id: string;
  cliente: string;
  documento_id: string;
  avviso: string;
  data_avviso: string;
  importo: string;
  modalita: string;
  nota: string;
  registrato_da: string;
}

export interface LavoroNonFatturato {
  pratica_id: string;
  pratica: string;
  cliente_id: string;
  cliente: string;
  minuti: number;
  da_fatturare: string;
  senza_tariffa: number;
}

export interface TotaliPeriodo {
  minuti: number;
  valore_ore: string;
  da_fatturare: string;
  senza_tariffa: number;
  avvisi: number;
  importo_avvisi: string;
  incassato: string;
  da_incassare: string;
  avvisi_aperti: number;
}

/** La scheda Periodo del Controllo: le somme sono quelle del server, la pagina non ricalcola niente. */
export interface PeriodoControllo {
  dal: string;
  al: string;
  cliente_id: string | null;
  anni: number[];
  totali: TotaliPeriodo;
  clienti: ClientePeriodo[];
  avvisi: AvvisoPeriodo[];
  incassi: IncassoPeriodo[];
  non_fatturato: LavoroNonFatturato[];
  /** Chi ha qualcosa nel periodo, senza il filtro cliente: per sceglierne un altro. */
  elenco_clienti: { cliente_id: string; nome: string }[];
}

/** Il costo orario di un dipendente: la riga di una storia che cambia nel tempo. */
export interface CostoRiga {
  id: number;
  oraria: string;
  dal: string;
  nota: string;
}

export interface PersonaCosti {
  persona: string;
  nome: string;
  ruolo: string;
  attivo: boolean;
  /** Il costo di oggi, o null se la persona non ne ha uno che valga da oggi. */
  in_vigore: string | null;
  costi: CostoRiga[];
}

export interface AvvisoAperto {
  documento_id: string;
  descrizione: string;
  data: string;
  netto: string;
  incassato: string;
  residuo: string;
}

export interface MovimentoCliente extends Incasso {
  descrizione_avviso: string;
}

/** Quanto un cliente deve ancora e come ha pagato fin qui. */
export interface SituazioneCliente {
  da_incassare: string;
  avvisi: AvvisoAperto[];
  movimenti: MovimentoCliente[];
}

/** Una fattura incassata che aspetta di essere emessa su Fatture Smart. */
export interface DaPassare {
  documento: Documento;
  giorni_di_attesa: number;
  /** Entro 12 giorni dall'incasso: "avviso" dall'ottavo, "scaduto" oltre. */
  urgenza: "in_tempo" | "avviso" | "scaduto";
}

export interface CampoScheda {
  etichetta: string;
  valore: string;
  nota: string;
}

/** Cosa copiare in Fatture Smart. Gli importi sono gia' scritti in italiano
 * dal server, come vanno incollati: qui non si formatta niente. */
/** Il testo di un avviso o di una fattura da incollare in Fatture Smart. */
export interface TestoFatturaSmart {
  testo: string;
  /** Si ritocca finche' il documento non e' partito. */
  modificabile: boolean;
  /** Come vanno scritti nei loro campi di Fatture Smart. */
  importo: string;
  iva: string;
  /** Imponibile, cassa, IVA e totale, di sola lettura: per riguardare la maschera di Fatture Smart. */
  controllo: CampoScheda[];
  totale_atteso: string;
  da_guardare: string[];
  /** Il testo piu' recente dello stesso cliente, col periodo avanzato. */
  ultima_volta: UltimaVolta | null;
}

export interface UltimaVolta {
  documento_id: string;
  tipo: string;
  data: string;
  numero: string | null;
  testo: string;
  periodo_avanzato: boolean;
  /** Gli anni scritti dentro le voci, da ricontrollare. */
  anni: string[];
}

export interface Scheda {
  documento: CampoScheda[];
  cliente: CampoScheda[];
  righe: { descrizione: string; importo: string; iva: string }[];
  riepilogo: CampoScheda[];
  pagamento: CampoScheda[];
  da_guardare: string[];
  testo: string;
}

export function nomeDocumento(tipo: TipoDocumento): string {
  const nomi: Record<TipoDocumento, string> = {
    AVVISO: "Avviso di parcella",
    FATTURA: "Fattura",
    NOTA_CREDITO: "Nota di credito",
  };
  return nomi[tipo];
}

/** Lo stato come lo dice una persona, e cambia col tipo: un avviso "emesso"
 * e' un avviso mandato al cliente e non ancora pagato, una fattura "emessa"
 * e' semplicemente una fattura. Chiamarli allo stesso modo fa credere che
 * l'avviso aspetti qualcosa che invece e' gia' successo, o il contrario. */
export function nomeStatoDocumento(tipo: TipoDocumento, stato: StatoDocumento): string {
  if (tipo === "AVVISO") {
    const nomi: Record<StatoDocumento, string> = {
      BOZZA: "in bozza",
      EMESSO: "mandato, da incassare",
      INCASSATO: "incassato",
      ANNULLATO: "annullato",
      STORNATO: "stornato",
    };
    return nomi[stato];
  }
  const nomi: Record<StatoDocumento, string> = {
    BOZZA: tipo === "FATTURA" ? "da passare su Fatture Smart" : "in bozza",
    EMESSO: "emessa",
    INCASSATO: "incassata",
    ANNULLATO: "annullata",
    STORNATO: "stornata",
  };
  return nomi[stato];
}

// ------------------------------------------------------------------ importi

/** Un importo del backend ("1234.50") scritto come su una parcella
 * ("1.234,50").
 *
 * **Solo operazioni su stringhe, mai `Number`.** E' la regola di `api.ts`
 * portata fino all'ultimo passo: un importo che passa per il float anche solo
 * per essere stampato e' un importo che un giorno si stampa sbagliato al
 * centesimo, ed e' proprio li' che il cliente guarda. */
export function euro(importo: string): string {
  const negativo = importo.startsWith("-");
  const nudo = negativo ? importo.slice(1) : importo;
  const [interi = "0", decimali = ""] = nudo.split(".");
  const gruppi: string[] = [];
  for (let fine = interi.length; fine > 0; fine -= 3) {
    gruppi.unshift(interi.slice(Math.max(0, fine - 3), fine));
  }
  const centesimi = (decimali + "00").slice(0, 2);
  return `${negativo ? "-" : ""}${gruppi.join(".") || "0"},${centesimi}`;
}

/** Quello che una persona scrive in un campo importo, nel formato che il
 * backend accetta: "1.234,5" diventa "1234.50". Restituisce `null` se il
 * testo non e' un importo, invece di indovinare.
 *
 * Si accettano tutti e due i separatori decimali perche' chi copia una cifra
 * da una parcella la copia com'e' stampata. **Se c'e' una virgola, i punti
 * sono migliaia**; se non c'e', un punto e' il decimale e ammette al massimo
 * due cifre dopo di se'. Quindi "1.000" da solo **si rifiuta**: potrebbe
 * voler dire mille o uno, e un campo che sceglie per chi scrive e' il modo di
 * mettere in parcella mille euro invece di uno. Scritto "1.000,00" o "1000"
 * non ci sono dubbi. */
export function importoDaScritto(testo: string): string | null {
  const pulito = testo.trim().replace(/\s/g, "").replace(/€/g, "");
  if (pulito === "") return null;
  const normale = pulito.includes(",")
    ? pulito.replace(/\./g, "").replace(",", ".")
    : pulito;
  if (!/^\d+(\.\d{1,2})?$/.test(normale)) return null;
  const [interi = "0", decimali = ""] = normale.split(".");
  const senzaZeri = interi.replace(/^0+(?=\d)/, "");
  return `${senzaZeri}.${(decimali + "00").slice(0, 2)}`;
}

/** Se un importo del backend e' zero. Confronto sul testo, per la stessa
 * ragione di `euro`: "0.00" e "0" sono zero, e non serve un float per saperlo. */
export function eZero(importo: string): boolean {
  return /^-?0*(\.0*)?$/.test(importo);
}

function centesimi(importo: string): bigint {
  const negativo = importo.startsWith("-");
  const nudo = negativo ? importo.slice(1) : importo;
  const [interi = "0", decimali = ""] = nudo.split(".");
  const valore = BigInt(interi || "0") * 100n + BigInt((decimali + "00").slice(0, 2));
  return negativo ? -valore : valore;
}

/** La differenza fra due importi, esatta al centesimo: `differenza("100.00", "30.50")`
 * e' "69.50". Negativa se il secondo e' piu' grande. */
export function differenza(a: string, b: string): string {
  const totale = centesimi(a) - centesimi(b);
  const negativo = totale < 0n;
  const assoluto = negativo ? -totale : totale;
  return `${negativo ? "-" : ""}${assoluto / 100n}.${String(assoluto % 100n).padStart(2, "0")}`;
}

/** Il segno della differenza: -1, 0 o 1. Per sapere se si e' incassato meno, quanto o
 * piu' del dovuto senza passare dai numeri a virgola mobile. */
export function confronta(a: string, b: string): number {
  const d = centesimi(a) - centesimi(b);
  return d < 0n ? -1 : d > 0n ? 1 : 0;
}

/** La somma di piu' importi, esatta al centesimo.
 *
 * Serve per il totale di cio' che si sta per mettere in un avviso e per
 * quanto lo studio aspetta di incassare. **Passa per i centesimi interi**
 * (`BigInt`), non per il float: sommare 0,10 venti volte in virgola mobile
 * non fa 2,00, e un totale che non combacia con le righe sopra e' quello che
 * fa dubitare di tutto il resto. */
export function somma(importi: string[]): string {
  const totale = importi.reduce((acc, x) => acc + centesimi(x), 0n);
  const negativo = totale < 0n;
  const assoluto = negativo ? -totale : totale;
  return `${negativo ? "-" : ""}${assoluto / 100n}.${String(assoluto % 100n).padStart(2, "0")}`;
}

/** Una data con l'anno: "2026-09-22" diventa "22 settembre 2026". Per i
 * documenti, dove l'anno conta: una fattura si cerca per anno. */
export function dataCompleta(iso: string): string {
  const [anno, mese, giorno] = iso.split("-").map(Number);
  if (anno === undefined || mese === undefined || giorno === undefined) return iso;
  return new Date(anno, mese - 1, giorno).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ------------------------------------------------------------------- tempo

/** I minuti come li legge una persona: 90 diventa "1h 30m".
 *
 * Si mostrano cosi' e si scrivono in minuti interi, mai in ore decimali:
 * un quarto d'ora e' 15, non 0,25. Le ore decimali fanno nascere 0,3333 e
 * poi differenze al centesimo su una parcella. */
export function oreEMinuti(minuti: number): string {
  const ore = Math.floor(minuti / 60);
  const resto = minuti % 60;
  if (ore === 0) return `${resto}m`;
  if (resto === 0) return `${ore}h`;
  return `${ore}h ${String(resto).padStart(2, "0")}m`;
}

/** Un conteggio con la sua parola al numero giusto: "1 cliente", "3 clienti".
 *
 * Esiste perche' "1 clienti" e "1 pratiche, 1 aperte" erano finiti a schermo,
 * ed e' il genere di sciatteria che fa sembrare improvvisato un programma
 * corretto. Chi lo usa tutti i giorni lo legge decine di volte. */
export function dataBreve(iso: string): string {
  const [anno, mese, giorno] = iso.split("-");
  return `${giorno}/${mese}/${anno}`;
}

/** I giorni passati da una data (oggi compreso nel conto: ieri e' 1). */
export function giorniDa(iso: string, adesso: Date = new Date()): number {
  const [anno, mese, giorno] = iso.split("-").map(Number);
  if (anno === undefined || mese === undefined || giorno === undefined) return 0;
  const inizio = Date.UTC(anno, mese - 1, giorno);
  const oggiUtc = Date.UTC(adesso.getFullYear(), adesso.getMonth(), adesso.getDate());
  return Math.round((oggiUtc - inizio) / 86_400_000);
}

export type UrgenzaFattura = "in_tempo" | "avviso" | "scaduto";

/** Quanto resta per emettere una fattura, dai giorni passati dall'incasso. Stessa regola
 * del server (`domain/incasso.py`): 12 giorni per legge, avviso dall'ottavo. */
export function scadenzaFattura(
  giorni: number,
  data: string,
): { testo: string; entro: string; urgenza: UrgenzaFattura } {
  const [anno, mese, giorno] = data.split("-").map(Number);
  const limite = new Date(anno ?? 1970, (mese ?? 1) - 1, (giorno ?? 1) + 12);
  const entro = limite.toLocaleDateString("it-IT", { day: "numeric", month: "long" });
  const mancano = 12 - giorni;
  const urgenza: UrgenzaFattura = giorni > 12 ? "scaduto" : giorni >= 8 ? "avviso" : "in_tempo";
  const testo =
    mancano < 0
      ? `scaduta da ${conta(-mancano, "giorno", "giorni")}`
      : mancano === 0
        ? "scade oggi"
        : `scade fra ${conta(mancano, "giorno", "giorni")}`;
  return { testo, entro, urgenza };
}

export function conta(quanti: number, singolare: string, plurale: string): string {
  return `${quanti} ${quanti === 1 ? singolare : plurale}`;
}

/** Una frazione (0,75) come si legge a schermo ("75%"). Arrotonda, non
 * tronca: 0,995 e' "100%" prima che "99%", perche' troncare farebbe leggere
 * "quota in giornata" come se mancasse sempre qualcosa anche quando e'
 * praticamente piena. */
export function percento(frazione: number): string {
  return `${Math.round(frazione * 100)}%`;
}

/** Una data ISO come si legge in italiano: "2026-09-22" diventa
 * "mar 22 settembre". Il giorno della settimana c'e' perche' chi compila le
 * ore ragiona per giornate, non per numeri. */
export function giornoLeggibile(iso: string): string {
  const [anno, mese, giorno] = iso.split("-").map(Number);
  if (anno === undefined || mese === undefined || giorno === undefined) return iso;
  const data = new Date(anno, mese - 1, giorno);
  return data.toLocaleDateString("it-IT", {
    weekday: "short",
    day: "numeric",
    month: "long",
  });
}

/** La data di oggi come "AAAA-MM-GG", presa dal calendario locale.
 *
 * **Non si usa `toISOString()`**, che converte prima in UTC: alle 01:00 di
 * notte in Italia restituirebbe il giorno prima, e un'imputazione fatta a
 * tarda sera finirebbe sulla giornata sbagliata senza che nessuno se ne
 * accorga. E' la stessa famiglia della trappola del fuso gia' trovata nel
 * salvataggio su database. */
export function oggi(): string {
  const d = new Date();
  const mese = String(d.getMonth() + 1).padStart(2, "0");
  const giorno = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mese}-${giorno}`;
}

/** La data di N giorni fa, nello stesso formato e con la stessa cautela. */
export function giorniFa(quanti: number): string {
  const d = new Date();
  d.setDate(d.getDate() - quanti);
  const mese = String(d.getMonth() + 1).padStart(2, "0");
  const giorno = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mese}-${giorno}`;
}
